import { FC, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { InstituteViewProps } from "./institute.interfaces";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  InputAdornment,
  Box,
  LinearProgress,
  Chip,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import {
  Search as SearchIcon,
  School as SchoolIcon,
  Add as AddIcon,
  CheckCircle,
  HourglassEmpty,
  Archive,
  EditNote,
  LabelOutlined,
} from "@mui/icons-material";
import MainTitle from "../../components/mainTitle";
import { useInstituteHook } from "../../../hooks/useInstituteHook";
import {
  TInstitute,
  TInstituteStatus,
  TInstituteType,
} from "../../../apis/institute/institute.interfaces";
import { useNavigate } from "react-router-dom";
import { pages } from "../../../utils/common";
import {
  formatInstituteStatusLabel,
  instituteStatusChipColor,
} from "./institute.helpers";
import dayjs from "dayjs";

const LIST_STATUS_FILTERS = [
  "all",
  "active",
  "pending",
  "draft",
  "other",
  "archived",
] as const;

type InstituteListStatusFilter = (typeof LIST_STATUS_FILTERS)[number];

const InstituteView: FC<InstituteViewProps> = () => {
  const { data, isLoading, error } = useInstituteHook();
  const { control, watch } = useForm<{ search: string }>({
    defaultValues: { search: "" },
  });
  const searchQuery = watch("search");
  const [statusFilter, setStatusFilter] =
    useState<InstituteListStatusFilter>("all");
  const [typeFilter, setTypeFilter] = useState<TInstituteType | "all">("all");
  const navigate = useNavigate();

  const filteredInstitutes = useMemo(() => {
    if (!data) return [];

    return data.filter((institute) => {
      const matchesSearch =
        institute.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        institute.registrationNumber
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

      const isSoftDeleted = !!institute.deletedAt;
      const matchesStatus =
        statusFilter === "all"
          ? !isSoftDeleted
          : statusFilter === "archived"
            ? isSoftDeleted || institute.status === "archived"
            : !isSoftDeleted && institute.status === statusFilter;
      const matchesType = typeFilter === "all" || institute.type === typeFilter;

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [data, searchQuery, statusFilter, typeFilter]);

  const getStatusIcon = (status: TInstituteStatus) => {
    switch (status) {
      case "active":
        return <CheckCircle color="success" />;
      case "pending":
        return <HourglassEmpty color="warning" />;
      case "draft":
        return <EditNote color="info" />;
      case "other":
        return <LabelOutlined color="action" />;
      case "archived":
        return <Archive color="disabled" />;
      default:
        return null;
    }
  };
  const handleInstituteClick = (institute: TInstitute) => {
    navigate(`${pages.institute.path}/${institute.id}`);
  };

  const handleCreateNew = () => {
    navigate(`${pages.institute.path}/create`);
  };

  const instituteTypes: TInstituteType[] = [
    "CBSE",
    "ICSE",
    "State Board",
    "IB",
    "IGCSE",
    "Other",
  ];

  if (isLoading) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.institute} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.institute} />
        <Box p={3}>
          <MainTitle title="Institutes" />
          <Alert severity="error">
            Failed to load institutes. Please try again later.
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.institute} />

      <Grid container spacing={3} data-testid="institute-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
            data-testid="institute-header"
          >
            <MainTitle title="Institutes" />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              size="large"
              data-testid="create-institute-button"
            >
              Create New Institute
            </Button>
          </Box>
        </Grid>

        {/* Search and Filters */}
        <Grid item xs={12}>
          <Card data-testid="institute-filters-card">
            <CardContent>
              <Grid container spacing={2}>
                <ControlledTextField
                  control={control}
                  name="search"
                  id="institute-search"
                  label="Search"
                  placeholder="Search by name or registration number..."
                  data-testid="institute-search-input"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <Grid item xs={12} md={3}>
                  <Box display="flex" gap={1} flexWrap="wrap" data-testid="institute-status-filter-box">
                    <Typography
                      variant="body2"
                      sx={{ alignSelf: "center", minWidth: "60px" }}
                    >
                      Status:
                    </Typography>
                    {LIST_STATUS_FILTERS.map((status) => (
                      <Chip
                        key={status}
                        label={
                          status === "all"
                            ? "All"
                            : formatInstituteStatusLabel(status)
                        }
                        onClick={() => setStatusFilter(status)}
                        color={statusFilter === status ? "primary" : "default"}
                        variant={
                          statusFilter === status ? "filled" : "outlined"
                        }
                        size="small"
                        data-testid={`institute-status-filter-${status.toLowerCase().replace(/\s+/g, "-")}`}
                      />
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Typography
                      variant="body2"
                      sx={{ alignSelf: "center", minWidth: "60px" }}
                    >
                      Type:
                    </Typography>
                    {(["all", ...instituteTypes] as const).map((type) => (
                      <Chip
                        key={type}
                        label={type}
                        onClick={() => setTypeFilter(type)}
                        color={typeFilter === type ? "primary" : "default"}
                        variant={typeFilter === type ? "filled" : "outlined"}
                        size="small"
                        data-testid={`institute-type-filter-${type === "all" ? "all" : type.toLowerCase().replace(/\s+/g, "-")}`}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Institute List */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Institutes ({filteredInstitutes.length})
          </Typography>
        </Grid>

        {filteredInstitutes.length === 0 ? (
          <Grid item xs={12}>
            <Card data-testid="institute-empty-state">
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="200px"
                  gap={2}
                  data-testid="institute-empty-content"
                >
                  <SchoolIcon sx={{ fontSize: 64, color: "text.secondary" }} />
                  <Typography variant="h6" color="text.secondary">
                    {data?.length === 0
                      ? "No institutes available"
                      : "No institutes match your search criteria"}
                  </Typography>
                  {data?.length === 0 && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Get started by creating your first institute.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreateNew}
                        data-testid="create-institute-empty-button"
                      >
                        Create New Institute
                      </Button>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          filteredInstitutes.map((institute) => {
            const showArchivedChrome =
              !!institute.deletedAt || institute.status === "archived";
            return (
              <Grid item xs={12} sm={6} md={6} lg={4} key={institute.id}>
                <Card
                  sx={{
                    cursor: "pointer",
                    border: "1px solid",
                    borderStyle: showArchivedChrome ? "dashed" : "solid",
                    borderColor: showArchivedChrome ? "text.disabled" : "divider",
                    bgcolor: showArchivedChrome ? "action.hover" : "background.paper",
                    opacity: showArchivedChrome ? 0.7 : 1,
                    filter: showArchivedChrome ? "grayscale(0.5)" : "none",
                    transition: "opacity 0.2s, filter 0.2s",
                    "&:hover": {
                      boxShadow: 3,
                      opacity: showArchivedChrome ? 0.9 : 1,
                      filter: showArchivedChrome ? "grayscale(0.2)" : "none",
                    },
                  }}
                  onClick={() => handleInstituteClick(institute)}
                  data-testid={`institute-card-${institute.id}`}
                >
                  <CardContent>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={2}
                      data-testid="each-institute-name-and-status-box"
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <SchoolIcon color="primary" />
                        <Typography variant="h6" component="div">
                          {institute.name}
                        </Typography>
                      </Box>
                      {showArchivedChrome ? (
                        <Archive color="disabled" />
                      ) : (
                        getStatusIcon(institute.status)
                      )}
                    </Box>

                    <Box mb={2}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Type: {institute.type}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Registration: {institute.registrationNumber}
                      </Typography>
                      {institute.registrationDate ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Registered:{" "}
                          {dayjs(institute.registrationDate).format("MM/DD/YYYY")}
                        </Typography>
                      ) : null}
                    </Box>

                    <Box data-testid="each-institute-status-chip-box"
                      sx={{ mb: 2, display: "flex", gap: 1 }}>
                      <Chip
                        label={formatInstituteStatusLabel(institute.status)}
                        color={instituteStatusChipColor(institute.status)}
                        size="small"
                      />
                      <Chip
                        label={institute.type}
                        variant="outlined"
                        size="small"
                      />
                    </Box>

                    <Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Configuration Completeness
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {institute.configurationCompleteness}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={institute.configurationCompleteness}
                        color={
                          institute.configurationCompleteness >= 80
                            ? "success"
                            : institute.configurationCompleteness >= 50
                              ? "warning"
                              : "error"
                        }
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        )}
      </Grid>
    </>
  );
};

export default InstituteView;
